<template>
  <div class="card overflow-hidden">
    <div class="card-body pt-3 pb-0 pe-0">
      <div class="row">
        <div class="col-12 d-flex justify-content-between">
          <div v-if="period" class="pt-2 text-lighter">
            <i class="fa fa-clock"></i
            ><span class="ms-2 font-weight-bold">{{
              formatPeriod(period)
            }}</span>
          </div>
          <div class="d-flex">
            <div class="form-group mx-3">
              <SectionDropdown
                id="ddSectionFilterStrainWorkouts"
                :selected="selectedSection"
                @selected-section-id="
                  (selectedValue) => (selectedSection = selectedValue)
                "
              />
            </div>
          </div>
        </div>
        <div class="col-lg-8 col-12">
          <div class="text-xs text-uppercase text-primary text-bold mb-1">
            {{ header }}
          </div>
          <span class="h5 mt-2 text-uppercase">{{
            getSectionName(selectedSection)
          }}</span>

          <div v-if="!hasWorkoutData" class="text-muted py-3">
            No data found for the selected date range.
          </div>

          <div v-show="hasWorkoutData" class="mt-4 chart">
            <canvas :id="id" class="chart-canvas"></canvas>
          </div>
        </div>

        <div v-if="hasWorkoutData" class="col-lg-4 col-12">
          <div
            class="d-flex flex-column mx-3 position-relative mt-sm-4 ms-sm-0 help"
          >
            <div class="text-bolder mb-2">Max Heart Rate</div>
            <div class="mb-3 content">
              In workouts, understanding your max HR can be useful for
              establishing target heart rate zones during different types of
              exercises. Heart rate zones help individuals optimize their
              workouts based on specific fitness goals, such as fat burning,
              aerobic conditioning, or high-intensity interval training.
            </div>
            <div class="content">
              It is important to note that while max HR is a useful reference,
              individual variations exist, and factors such as fitness level,
              health conditions, and medications can influence heart rate
              responses during exercise.
            </div>
            <div class="text-bolder mt-3 mb-2">Active Calories</div>
            <div class="mb-3 content">
              When you engage in activities like walking, running, cycling, or
              any form of exercise, your body expends energy, and the number of
              calories burned during these activities is considered active
              calories.
            </div>
            <div class="content">
              The actual number of active calories burned depends on various
              factors such as the type of activity, duration, intensity, and
              individual characteristics like weight and fitness level.
            </div>
          </div>
        </div>

        <div v-if="hasWorkoutData" class="row bg-dark mt-4">
          <div class="d-flex flex-wrap justify-content-center mt-4">
            <workout-types
              :data-workout-types="dataWorkoutTypes"
              @selected-workout-type-id="
                (selectedValue) => (selectedWorkoutType = selectedValue)
              "
            />
          </div>

          <!-- Workouts Table -->
          <div v-if="hasWorkoutData" class="col-12 pt-3">
            <div class="table-responsive">
              <el-table
                ref="dataTable"
                :data="dataWorkouts.rows"
                style="width: 100% !important"
                @sort-change="sortColumn"
                @row-click="handleWorkoutRowClick"
              >
                <el-table-column
                  v-for="column in getColumnHeaders(selectedWorkoutType)"
                  :key="column.prop"
                  :prop="column.prop"
                  :label="column.label"
                  :formatter="column.formatter"
                  :min-width="column.minWidth"
                  :width="column.width"
                  :class-name="column.class"
                  sortable="custom"
                  label-class-name="text-capitalize"
                >
                  <template v-if="column.prop == 'strain'" #default="scope">
                    <argon-progress
                      color="success"
                      :percentage="(scope.row.strain / 21) * 100"
                      :left-text="scope.row.strain.toFixed(1)"
                    />
                  </template>
                </el-table-column>

                <el-table-column label="" width="60" prop="actions">
                  <template #default="scope">
                    <div class="d-flex">
                      <router-link
                        target="_blank"
                        :to="{
                          name: `User`,
                          params: { udid: scope.row.udid },
                          query: { date: getDate(scope.row.workout_record_end_time)},
                        }"
                        exact
                      >
                        <icon-user-info />
                      </router-link>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card-footer paginator">
      <div
        v-if="hasWorkoutData"
        class="d-flex justify-content-between align-items-center"
      >
        <span class="ms-3 text-muted">{{ dataWorkouts.count }} record(s)</span>
        <app-pagination
          :records="dataWorkouts.count"
          :page-size="pageSize"
          :page="page"
          @update-page-selection="
            (selectedValue) => updatePageSelection(selectedValue)
          "
        />
      </div>
    </div>
  </div>

  <!-- workout details Modal -->
  <modal
      id="workoutDetailsModal"
      v-model:show="workoutDetailsModal"
      class="modal fade"
      footer-classes="justify-content-end align-items-center"
      :show-close="true"
      :modalContentClasses="'workout-intense-model'"
    >
      <template #header>
        <div class="header-container">
          <div class="text-xl text-capitalize">{{ this.selectedWorkoutName }}</div>
        </div>
      </template>

      <div class="p-2">
          <WorkoutSummary  v-if="this.selectedWorkoutRow" :user="this.selectedWorkoutRow.user[0]"
              :workout-data="this.selectedWorkoutRow"></WorkoutSummary>
      </div>
      <template #footer>
        <button type="button" class="btn btn-primary"
          @click="closeModal">DONE</button>
      </template>
  </modal>
</template>

<script>
const dayjs = require("dayjs");
const duration = require("dayjs/plugin/duration");
dayjs.extend(duration);

import Constants from "@/helpers/constants";
import Utils from "@/helpers/utils";
import DateUtils from "@/helpers/dateUtils";
import ChartUtils from "@/helpers/chartUtils";
import Modal from "@/shared/Modal.vue";
import ArgonProgress from "@/shared/Controls/ArgonProgress.vue";
import IconUserInfo from "@/shared/Controls/Icons/IconUserInfo.vue";
import Chart from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import SectionDropdown from "@/views/pages/components/SectionDropdown.vue";
import WorkoutTypes from "@/views/pages/components/WorkoutTypes.vue";
import styles from "./StrainSectionWorkoutsChart.vue?vue&type=style&index=0&lang=scss&module=1";
import WorkoutSummary from "./insights/workout-details/WorkoutSummary.vue";

Chart.register(annotationPlugin);

export default {
  name: "StrainSectionWorkoutsChart",
  components: {
    ArgonProgress,
    IconUserInfo,
    SectionDropdown,
    WorkoutTypes,
    Modal,
    WorkoutSummary,
  },
  props: {
    id: {
      type: String,
      default: "strain-sections-workout-chart",
    },
    period: {
      type: Array,
      required: true,
    },
    header: {
      type: String,
      default: "Strain Workouts Chart",
    },
    title: {
      type: String,
      default: "Today",
    },
    height: {
      type: [String, Number],
      default: "200",
    },
    selectedSectionId: {
      type: Number,
      default: -1,
    },
    selectedWorkoutTypeId: {
      type: Number,
      default: -1,
    },
    dataWorkoutTypes: {
      type: Array,
      required: true,
    },
    dataWorkouts: {
      type: Object,
      required: true,
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
        color: String,
      },
    },
  },
  emits: [
    "selected-section-id",
    "selected-workout-type-id",
    "selected-page",
    "selected-sort",
    "selected-order",
  ],
  data() {
    return {
      selectedSection: Constants.DEFAULT_SECTION,
      selectedWorkoutType: -1,
      page: Constants.DEFAULT_PAGE,
      pageSize: Constants.DEFAULT_PAGE_SIZE,
      alerts: [],
      annotationsAvg: [],
      workoutDetailsModal: false,
      selectedWorkoutRow : null,
      selectedWorkoutName: '',
    };
  },
  computed: {
    hasWorkoutData() {
      return this.dataWorkouts && this.dataWorkouts.count > 0;
    },
  },
  watch: {
    chart() {
      if (this.chart && this.chart.datasets.length > 0) {
        this.addChart();
      }
    },
    period() {
      this.selectedWorkoutType = -1;
      this.updatePageSelection(Constants.DEFAULT_PAGE);
    },
    selectedWorkoutTypeId(newValue) {
      // from parent
      this.selectedWorkoutType = newValue;
      this.updatePageSelection(Constants.DEFAULT_PAGE);
    },
    selectedWorkoutType() {
      // from child
      this.$emit("selected-workout-type-id", this.selectedWorkoutType);
    },
    selectedSection() {
      this.$emit("selected-section-id", this.selectedSection);
    },
    selectedSectionId() {
      this.selectedSection = this.selectedSectionId;
    },
  },
  methods: {
    sortColumn({ prop, order }) {
      this.$emit("selected-sort", prop);
      this.$emit("selected-order", order == "ascending" ? "asc" : "desc");
    },
    getSectionName(sectionId) {
      const section = this.$store.getters["list/getSectionName"](sectionId);
      return section ? section.name : "";
    },
    formatPeriod(period) {
      return Utils.formatPeriod(period);
    },
    getAvgPace(durationMs, distanceM) {
      if (durationMs > 0 && distanceM > 0) {
        let pace = durationMs / (distanceM * 0.001);
        let displayFormat = pace > 60 * 60 * 1000 ? "H:mm:s" : "mm:s";
        return dayjs.duration(pace).format(displayFormat);
      }
      return "-";
    },
    getSwimLaps(distanceM, swimPoolLengthCm) {
      if (distanceM > 0 && swimPoolLengthCm > 0) {
        return (distanceM * 100) / swimPoolLengthCm;
      } else return "-";
    },
    getAvgStrokesRate(durationMs, swimPullTimes) {
      if (durationMs > 0 && swimPullTimes > 0) {
        let avg = swimPullTimes / (durationMs / 1000 / 60);
        return avg.toFixed(2);
      } else return "-";
    },
    getAvgCadence(durationMs, strokes) {
      let avgCadence = (strokes * 60) / (durationMs / 1000);
      return avgCadence > 0 ? avgCadence.toFixed(2) : 0;
    },
    getColumnHeaders(workoutType) {
      return ChartUtils.getColumnHeaders(workoutType);
    },
    average(ctx) {
      const values = ctx.chart.data.datasets[1].data;
      let average = values.reduce((a, b) => a + b, 0) / values.length;
      return average;
    },
    addChart() {
      const ctx = document.getElementById(this.id).getContext("2d");

      let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);

      gradientStroke1.addColorStop(1, "rgba(172,0,0,0.15)");
      gradientStroke1.addColorStop(0, "transparent");

      let chartStatus = Chart.getChart(this.id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }

      new Chart(ctx, {
        data: {
          labels: this.chart.labels,
          datasets: [
            {
              type: "bar",
              label: this.chart.datasets[0].label,
              data: this.chart.datasets[0].data,
              pointBackgroundColor: styles.lightRed,
              borderColor: styles.lightRed,
              backgroundColor: styles.lightRed,
              order: 2,
              yAxisID: "y1",
              tension: 0.4,
              borderWidth: 1,
              borderRadius: 4,
              borderSkipped: false,
              maxBarThickness: 10,
            },
            {
              type: "line",
              label: this.chart.datasets[1].label,
              data: this.chart.datasets[1].data,
              tension: 0.4,
              borderWidth: 3,
              pointRadius: 3,
              pointBackgroundColor: styles.colorStrain,
              borderColor: styles.colorStrain,
              backgroundColor: gradientStroke1,
              fill: true,
              order: 1,
              yAxisID: "y",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            annotation: {
              annotations: {
                line1: {
                  type: "line",
                  borderColor: styles.colorCaloriesLimitline,
                  borderDash: [6, 6],
                  borderDashOffset: 0,
                  borderWidth: 1,
                  label: {
                    display: true,
                    content: (ctx) =>
                      "Avg: " + this.average(ctx).toFixed(0) + " kcal",
                    position: "start",
                  },
                  scaleID: "y",
                  value: (ctx) => this.average(ctx),
                },
              },
            },
            legend: {
              position: "bottom",
              labels: {
                color: styles.colorAxes,
                boxWidth: 12,
                boxHeight: 12,
                padding: 40,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y1: {
              display: true,
              position: "left",
              ticks: {
                stepSize: 100,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chart.datasets[0].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: false, // only want the grid lines for one axis to show up
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: styles.gray,
                padding: 10,
                font: {
                  size: 12,
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
            y: {
              display: true,
              position: "right",
              min: 0,
              beginAtZero: true,
              ticks: {
                stepSize: 100,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chart.datasets[1].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: true,
              },
            },
          },
        },
      });
    },
    updatePageSelection(selectedPage) {
      this.page = selectedPage;
      this.$emit("selected-page", selectedPage);
    },
    getDate(dateTime) {
      let timestamp = parseInt(dateTime);
     return DateUtils.formatDateToBucketDate(timestamp);
    },
    handleWorkoutRowClick(row, column) {
        if (column?.property === 'actions') return;
        
        this.selectedWorkoutRow = null;
        this.workoutDetailsModal = !this.workoutDetailsModal;
        this.selectedWorkoutRow = row;
        this.selectedWorkoutName = Utils.getWorkoutDescriptionFromID(row.workout_type, this.dataWorkoutTypes);
      },
      closeModal() {
        this.selectedWorkoutRow = null;
        this.workoutDetailsModal = false;
      },
  },
};
</script>

<style lang="scss" scoped>
:export {
  primary: $primary;
  darkRed: $g-red_900;
  red: $g-red_500;
  lightRed: $g-red_100;
  gray: $color-muted-text;
  darkGray: $color-gridlines;
  colorStrain: $color-strain;
  colorAxes: $color-chart-axes-text;
  colorCaloriesLimitline: $color-muted-text;
}

.workout-type-pill {
  border-radius: 24px;
  border: 2px solid white;

  &.is-active {
    border-color: $primary;
  }
  &:hover {
    border-color: $primary;
  }
}
</style>
